/**
 * Session Timeout Warning
 * 
 * Monitors the session expiry and shows a warning modal when the session
 * is about to expire. If the user is actively using the app, it will
 * auto-extend silently. If the user is idle, it shows a countdown modal
 * asking if they're still working.
 * 
 * - "Continue Working" → force refresh + extend session
 * - "Log Out" → clean logout
 * - Countdown hits 0 → auto-logout
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// How long before session expiry to show the warning (5 minutes)
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;

// How often to check session expiry (every 30 seconds)
const CHECK_INTERVAL_MS = 30 * 1000;

// Consider user "idle" after this period of no activity (2 minutes)
const IDLE_THRESHOLD_MS = 2 * 60 * 1000;

export function SessionTimeoutWarning() {
  const { isAuthenticated, sessionExpiresAt, extendSession, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0); // seconds remaining
  const [isExtending, setIsExtending] = useState(false);

  // Track last user activity
  const lastActivityRef = useRef<number>(Date.now());
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Update last activity timestamp on user interaction
  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Register activity event listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const;
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, handleActivity]);

  // Check if user is currently active (had activity within idle threshold)
  const isUserActive = useCallback(() => {
    return Date.now() - lastActivityRef.current < IDLE_THRESHOLD_MS;
  }, []);

  // Handle "Continue Working" button
  const handleContinueWorking = useCallback(async () => {
    setIsExtending(true);
    try {
      const success = await extendSession();
      if (success) {
        setShowWarning(false);
        setCountdown(0);
        // Clear countdown interval
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      } else {
        // Extension failed - force logout
        console.warn('Session extension failed, logging out');
        await logout();
      }
    } catch {
      console.error('Error extending session');
      await logout();
    } finally {
      setIsExtending(false);
    }
  }, [extendSession, logout]);

  // Handle "Log Out" button
  const handleLogout = useCallback(async () => {
    setShowWarning(false);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    await logout();
  }, [logout]);

  // Start the countdown timer when warning is shown
  const startCountdown = useCallback((secondsRemaining: number) => {
    setCountdown(Math.max(0, Math.floor(secondsRemaining)));
    
    // Clear any existing countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Time's up - auto logout
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          // Use setTimeout to avoid state update during render
          setTimeout(() => {
            logout();
          }, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [logout]);

  // Main session expiry check
  useEffect(() => {
    if (!isAuthenticated || !sessionExpiresAt) {
      // Clean up if not authenticated
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    const checkSessionExpiry = async () => {
      const now = Date.now();
      const expiresAt = new Date(sessionExpiresAt).getTime();
      const timeRemaining = expiresAt - now;

      // Session already expired
      if (timeRemaining <= 0) {
        setShowWarning(false);
        await logout();
        return;
      }

      // Within warning threshold
      if (timeRemaining <= WARNING_THRESHOLD_MS) {
        if (isUserActive() && !showWarning) {
          // User is actively working - silently extend
          console.log('User is active, auto-extending session');
          const success = await extendSession();
          if (!success) {
            // Auto-extend failed, show the warning
            setShowWarning(true);
            startCountdown(timeRemaining / 1000);
          }
        } else if (!showWarning) {
          // User is idle - show warning modal
          setShowWarning(true);
          startCountdown(timeRemaining / 1000);
        }
      }
    };

    // Run immediately
    checkSessionExpiry();

    // Set up interval
    checkIntervalRef.current = setInterval(checkSessionExpiry, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, sessionExpiresAt, isUserActive, extendSession, logout, showWarning, startCountdown]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  // Don't render if not showing warning
  if (!showWarning) return null;

  // Format countdown as M:SS
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Progress percentage (5 minutes = 300 seconds total)
  const totalWarningSeconds = WARNING_THRESHOLD_MS / 1000;
  const progressPercent = Math.max(0, Math.min(100, (countdown / totalWarningSeconds) * 100));

  // Color transitions: turquoise → yellow → rust as time runs out
  const getProgressColor = () => {
    if (progressPercent > 50) return 'bg-turquoise dark:bg-aqua';
    if (progressPercent > 25) return 'bg-yellow dark:bg-yellow';
    return 'bg-rust dark:bg-salmon';
  };

  const getCountdownColor = () => {
    if (progressPercent > 50) return 'text-turquoise dark:text-aqua';
    if (progressPercent > 25) return 'text-yellow dark:text-yellow';
    return 'text-rust dark:text-salmon';
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md mx-4 bg-white dark:bg-navy rounded-xl shadow-2xl animate-slide-in overflow-hidden">
        {/* Progress bar at top */}
        <div className="h-1.5 bg-gray-light dark:bg-midnight w-full">
          <div
            className={`h-full transition-all duration-1000 ease-linear rounded-r ${getProgressColor()}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Content */}
        <div className="px-8 pt-8 pb-6">
          {/* Icon */}
          <div className="flex justify-center mb-5">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              progressPercent > 25 
                ? 'bg-pond/10 dark:bg-pond-light/10' 
                : 'bg-rust/10 dark:bg-salmon/10'
            }`}>
              <Clock className={`w-8 h-8 ${
                progressPercent > 25 
                  ? 'text-pond dark:text-pond-light' 
                  : 'text-rust dark:text-salmon animate-pulse'
              }`} />
            </div>
          </div>

          {/* Heading */}
          <h2 className="text-xl font-semibold text-center text-slate dark:text-mist mb-2">
            Are you still working?
          </h2>

          {/* Description */}
          <p className="text-sm text-center text-chalkboard dark:text-rain-cloud mb-6">
            Your session is about to expire due to inactivity. 
            Click below to continue working.
          </p>

          {/* Countdown */}
          <div className="flex flex-col items-center mb-8">
            <span className={`text-4xl font-mono font-bold tabular-nums ${getCountdownColor()}`}>
              {timeDisplay}
            </span>
            <span className="text-xs text-chalkboard dark:text-rain-cloud mt-1">
              time remaining
            </span>
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleContinueWorking}
              disabled={isExtending}
              className="btn btn-primary w-full flex items-center justify-center gap-2 py-3 text-base disabled:opacity-70"
            >
              {isExtending ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Extending session...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Continue Working
                </>
              )}
            </button>

            <button
              onClick={handleLogout}
              disabled={isExtending}
              className="btn btn-ghost w-full flex items-center justify-center gap-2 py-2.5 text-sm text-chalkboard dark:text-rain-cloud hover:text-rust dark:hover:text-salmon"
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-8 py-3 bg-pale-gray-2 dark:bg-midnight/50 border-t border-gray-light dark:border-night-pond">
          <p className="text-xs text-center text-chalkboard/70 dark:text-rain-cloud/70">
            Sessions are automatically extended while you&apos;re actively using Slope.
          </p>
        </div>
      </div>
    </div>
  );
}
